/**
 * KitTimeline -- the RECORD-PAGE archetype's timeline-dominant center column
 * (zone 4): the chronicle.
 *
 * Law homes (cited, never re-derived here):
 *  - RECORD-PAGE archetype, zone 4 TIMELINE-DOMINANT CENTER: the ONE
 *    chronological, filterable spine of every touch IS the main column,
 *    promoted -- no card stack above or below it besides the honesty footer.
 *    Filter row grammar (All / <channel> / Calls / Notes / More filters), date
 *    groups, the single-line entry anatomy (icon | type | detail | ACTOR
 *    column | rel-abs IST time), latest-accent, routine dimming, inline
 *    expanders, and the "Showing N of N touches" footer are the PRIMITIVE's
 *    own state variants, inherited by every adopter, never re-implemented per
 *    host. docs/design/design-language/07-BEHAVIOURAL-LAWS.md ("### ARCH",
 *    UPDATE 2, zone 4).
 *  - FIVE-SECOND LAW (07 "### 5SEC"): the timeline is the record's heart -- the
 *    full story reads in place.
 *  - ANNOTATION-LAYER RIDER (07 "### ARCH" UPDATE 2): mockup annotation boxes
 *    are instruction-layer and are NEVER rendered as UI.
 *
 * REUSE, NEVER FORK: this wraps the ONE shared activity-timeline primitive
 * (ui/Timeline) in the archetype's center-column section frame. The built
 * entries + the windowed wire state (status / totalCount / hasMore /
 * onLoadMore / loadingMore / onRetry) ALL arrive via props -- this component
 * builds no entries and fetches nothing (a page-specific entry adapter maps
 * that surface's lawful read onto TimelineEntry[] BEFORE calling this).
 */
import type { ReactNode } from 'react';
import Timeline, { type TimelineProps } from '@/dashboard/ui/Timeline';

export interface KitTimelineProps extends TimelineProps {
  /** The center-column section wrapper className. Archetype default. */
  readonly sectionClassName?: string;
  /** The section's aria-label. */
  readonly sectionAriaLabel?: string;
  /** The section's anchor id (kept stable so existing jump-links still land). */
  readonly sectionId?: string;
  /** The section's data-testid (the archetype's timeline zone marker). */
  readonly sectionTestId?: string;
}

export default function KitTimeline({
  sectionClassName = 'lead-record-timeline',
  sectionAriaLabel = 'Activity timeline',
  sectionId = 'lead-record-timeline',
  sectionTestId = 'record360-zone-timeline',
  ...timeline
}: KitTimelineProps): JSX.Element {
  const wrapped: ReactNode = <Timeline {...timeline} />;
  return (
    <section
      className={sectionClassName}
      aria-label={sectionAriaLabel}
      id={sectionId}
      data-testid={sectionTestId}
    >
      {wrapped}
    </section>
  );
}
